import React, { useState, useEffect, useCallback } from "react";
import { signOut } from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { auth, db, storage } from "./firebase";
import { motion } from "framer-motion";
import { QRCodeCanvas } from "qrcode.react";
import {
  FaSignOutAlt,
  FaTrash,
  FaQrcode,
  FaFileUpload,
  FaCopy,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Main = ({ username, onLogout }) => {
  const [file, setFile] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [qrModal, setQrModal] = useState({ visible: false, fileURL: "" });

  const fetchFiles = useCallback(async () => {
    try {
      const q = query(collection(db, "files"), where("username", "==", username));
      const querySnapshot = await getDocs(q);
      const fetchedFiles = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setFiles(fetchedFiles);
    } catch (error) {
      toast.error("Failed to fetch files.");
    }
  }, [username]);

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    const storageRef = ref(storage, `${username}/${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setProgress(progress);
      },
      (error) => {
        toast.error("File upload failed.");
        setUploading(false);
      },
      async () => {
        const fileURL = await getDownloadURL(uploadTask.snapshot.ref);
        await addDoc(collection(db, "files"), {
          username,
          fileName: file.name,
          fileURL,
          fileSize: file.size,
          createdAt: Date.now(),
        });
        toast.success("File uploaded successfully!");
        setFile(null);
        setProgress(0);
        setUploading(false);
        fetchFiles();
      }
    );
  };

  const handleDelete = async (fileId, filePath) => {
    try {
      await deleteDoc(doc(db, "files", fileId));
      const fileRef = ref(storage, filePath);
      await deleteObject(fileRef);
      toast.success("File deleted successfully.");
      fetchFiles();
    } catch (error) {
      toast.error("Failed to delete file.");
    }
  };

  const handleCopyLink = (fileURL) => {
    navigator.clipboard.writeText(fileURL);
    toast.info("Link copied to clipboard!");
  };

  const getTimeRemaining = (createdAt) => {
    const timeLeft = 24 * 60 * 60 * 1000 - (Date.now() - createdAt);
    if (timeLeft <= 0) return "Expired";
    const hours = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
    return `${hours}:${minutes}:${seconds}`;
  };

  useEffect(() => {
    fetchFiles();

    const interval = setInterval(() => {
      setFiles((prevFiles) =>
        prevFiles.map((file) => ({
          ...file,
          timeRemaining: getTimeRemaining(file.createdAt),
        }))
      );
    }, 1000);

    return () => clearInterval(interval);
  }, [fetchFiles]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <ToastContainer />
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Welcome, {username}</h1>
        <button
          onClick={() => {
            signOut(auth);
            onLogout();
          }}
          className="flex items-center bg-red-600 text-white px-2 py-2 rounded-lg hover:bg-red-700"
        >
          <FaSignOutAlt className="mr-2" />
          
        </button>
      </div>

      {/* Content Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Upload Section */}
        <div className="bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4">Upload Your Files</h2>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              setFile(e.dataTransfer.files[0]);
            }}
            className={`border-2 border-dashed p-6 rounded-lg transition ${
              dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
            }`}
          >
            <p className="text-center text-gray-500">
              {file ? `Selected File: ${file.name}` : "Drag & Drop your file here or click below"}
            </p>
          </div>
          <input
            id="file-upload"
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            className="hidden"
          />
          <label
            htmlFor="file-upload"
            className="block mt-4 w-full bg-blue-500 text-white py-2 rounded-lg text-center cursor-pointer hover:bg-blue-600"
          >
            <FaFileUpload className="inline-block mr-2" />
            Select File
          </label>
          {uploading && (
            <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
              <div
                style={{ width: `${progress}%` }}
                className="bg-blue-500 h-2 rounded-full"
              />
            </div>
          )}
          <button
            onClick={handleUpload}
            className="w-full mt-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition disabled:opacity-50"
            disabled={!file || uploading}
          >
            {uploading ? "Uploading..." : "Upload File"}
          </button>
        </div>

        {/* Files List */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Your Files</h2>
          {files.length === 0 ? (
            <p className="text-gray-600">No files uploaded yet.</p>
          ) : (
            <ul className="divide-y divide-gray-300">
              {files.map((file) => (
                <li key={file.id} className="py-4 flex justify-between items-center">
                  <div>
                    <a
                      href={file.fileURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:underline"
                    >
                      {file.fileName}
                    </a>
                    <p className="text-gray-500 text-sm">
                      Size: {file.fileSize} bytes
                    </p>
                    <p className="text-sm text-red-500 font-semibold">
                      Expires in: {file.timeRemaining || "Expired"}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => setQrModal({ visible: true, fileURL: file.fileURL })}
                      className="bg-green-500 text-white px-3 py-1 rounded-lg hover:bg-green-600"
                    >
                      <FaQrcode />
                    </button>
                    <button
                      onClick={() => handleCopyLink(file.fileURL)}
                      className="bg-blue-500 text-white px-3 py-1 rounded-lg hover:bg-blue-600"
                    >
                      <FaCopy />
                    </button>
                    <button
                      onClick={() =>
                        handleDelete(file.id, `${username}/${file.fileName}`)
                      }
                      className="text-red-500 hover:text-red-600"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Rules or How to Use Section */}
      <div className="bg-gray-100 mt-8 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-bold text-gray-800 mb-4">How to Use</h2>
        <ol className="list-decimal list-inside text-gray-700">
          <li>Drag and drop your file into the upload box or click to select a file.</li>
          <li>Uploaded files will appear on the right side of the screen.</li>
          <li>
            Use the provided options to view, copy the link, generate a QR code, or delete files.
          </li>
          <li>
            Files are stored temporarily and will expire 24 hours after uploading.
          </li>
          <li>Ensure your file does not exceed the permitted size limit.</li>
        </ol>
      </div>

      {/* Footer */}
      <footer className="mt-8 text-center">
        <p className="text-gray-600">
          Developed by Suvm<span className="text-red-500 font-bold">.</span>
        </p>
      </footer>

      {/* QR Code Modal */}
      {qrModal.visible && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setQrModal({ visible: false, fileURL: "" })}
        >
          <motion.div
            className="bg-white p-8 rounded-lg shadow-lg relative"
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">QR Code</h2>
            <QRCodeCanvas value={qrModal.fileURL} size={200} />
            <button
              onClick={() => setQrModal({ visible: false, fileURL: "" })}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              &times;
            </button>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Main;
